import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Divider,
useTheme,
Menu,
Rating,
TextareaAutosize,
} from '@mui/material';
import {
Delete,
Hotel,
Search,
Add as AddIcon,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
PictureAsPdf,
TableChart,
GetApp,
X,
Star,
Layers
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
APIBrancheNamesFilter,
APIRoomStore,
APIRoomUpdate,
APIRoomDelete,
APIFacilityTypeNames,
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
formatCreatedAtDate,
formatNumberWithComma,
formatNumberWithComma2,
getCategoriesNameById,
getStatusById,
getUserDetails,
recordsPerPage,
statusData
} from '../../utils/helpers';
import {
exportToCSV,
exportToExcel,
exportToPDF
} from '../../services/room-settings/RoomService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import { roomApiService } from '../../apiServices/RoomApiService';

const Room = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);
const [pageState, setPageState] = useState({
    pageData: []
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [formData, setFormData] = useState({
    floor_name_id: '',
    roomtype: '',
    capacity: '',
    exbedcapability: '1',
    defaultrate: '',
    roomdescription: '',
    status: '1'
});

const [filters, setFilters] = useState({
    floor_name_id: '',
    roomtype: '',
    capacity: '',
    status: '',
    dateFrom: '',
    dateTo: ''
});
const [errors, setErrors] = useState({});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// Additional dropdown data
const [categoryNamesData, setCategoryNamesData] = useState([]);

// Extra capability options
const extraCapabilityOptions = [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' }
];

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

    // Initialize the API service
    useEffect(() => {
        roomApiService.setToastFunction(showToast);
    }, [showToast]);

useEffect(() => {
    fetchData();
    fetchBranchNames();
    fetchCategoriesNames();
}, []);

const fetchBranchNames = async () => {
    try {
    const response = await axios.get(APIBrancheNamesFilter);
    if (response.data.status === 'success') {
        setBranchNamesFilterData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching branch names:", error);
    showToast("Error", "Failed to load branch names", "error");
    }
};

const fetchCategoriesNames = async () => {
    try {
    const response = await axios.get(APIFacilityTypeNames);
    if (response.data.status === 'success') {
        setCategoryNamesData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching floor names:", error);
    showToast("Error", "Failed to load floor names", "error");
    }
};

    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            // For super admin, use selected branch or current user's branch
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            // Use the roomApiService to fetch data
            const response = await roomApiService.list(branchIdToUse);
            
            if (response.success) {
                const rooms = Array.isArray(response.data) ? response.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: rooms
                }));
                showToast("Success", response.message || TEXT_DATA_LOADED, "success");
            } else {
                setError(response.message);
                showToast("Error", response.message, "error");
            }
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
    // Also fetch floor names for the selected branch
    if (branchId) {
        fetchFloorNamesForBranch(branchId);
    }
};

const fetchFloorNamesForBranch = async (branchId) => {
    try {
        const params = { branch_id: branchId };
        const response = await axios.get(APIFacilityTypeNames, { params });
        if (response.data.status === 'success') {
            setCategoryNamesData(response.data.data || []);
        }
    } catch (error) {
        console.error("Error fetching floor names for branch:", error);
    }
};

// Filter based on search term and filters
const filteredData = pageState.pageData.filter(record => {
    const matchesSearch = searchTerm === '' ||
    record.roomtype.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (record.capacity && record.capacity.toString().includes(searchTerm)) ||
    record.status.toString().includes(searchTerm);

    const matchesFloor = !filters.floor_name_id ||
    record.floor_name_id?.toString() === filters.floor_name_id.toString();

    const matchesType = !filters.roomtype ||
    (record.roomtype && record.roomtype.toLowerCase().includes(filters.roomtype.toLowerCase()));

    const matchesCapacity = !filters.capacity ||
    (record.capacity && record.capacity.toString().includes(filters.capacity));

    const matchesStatus = !filters.status ||
    record.status.toString().includes(filters.status);

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate <= filterToDate;
    }

    return matchesSearch && matchesFloor && matchesType && matchesCapacity && matchesStatus && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    floor_name_id: '',
    roomtype: '',
    capacity: '',
    exbedcapability: '1',
    defaultrate: '',
    roomdescription: '',
    status: '1'
    });
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");

    // Set form data
    setFormData({
    floor_name_id: record.floor_name_id || '',
    roomtype: record.roomtype || '',
    capacity: record.capacity || '',
    exbedcapability: record.exbedcapability || '1',
    defaultrate: record.defaultrate || '',
    roomdescription: record.roomdescription || '',
    status: record.status || '1'
    });
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Reset error for this field
    setErrors(prev => ({
    ...prev,
    [name]: false
    }));

    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    setFilterOptions(false);
};

const clearFilters = () => {
    setFilters({
    floor_name_id: '',
    roomtype: '',
    capacity: '',
    status: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    setFilterOptions(false);
};

// Export handlers
const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
const handleExportClose = () => { setExportAnchorEl(null); };

// In your component, update the export handlers:
const handleExportExcel = async () => {
  try {
    handleExportClose();
    await exportToExcel(filteredData, categoryNamesData, [], branchNamesFilterData);
    showToast("Success", "Excel file downloaded successfully", "success");
  } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportPDF = async () => {
  try {
    handleExportClose();
    await exportToPDF(filteredData, categoryNamesData, [], branchNamesFilterData, {
      floor_name_id: filters.floor_name_id,
      roomtype: filters.roomtype,
      capacity: filters.capacity,
      status: filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo
    });
    showToast("Success", "PDF file downloaded successfully", "success");
  } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportCSV = async () => {
  try {
    handleExportClose();
    await exportToCSV(filteredData, categoryNamesData, [], branchNamesFilterData);
    showToast("Success", "CSV file downloaded successfully", "success");
  } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
  }
};

// Get selected branch name
const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

// Get floor name by ID
const getFloorNameById = (floorId) => {
    return getCategoriesNameById(floorId, categoryNamesData);
};

const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
    'floor_name_id',
    'roomtype', 
    'capacity', 
    'defaultrate', 
    'roomdescription'
    ];

    requiredFields.forEach(field => {
    if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = true;
    }
    });

    if (formData.capacity && (isNaN(formData.capacity) || parseInt(formData.capacity) <= 0)) {
    newErrors.capacity = true;
    }

    if (formData.defaultrate && (isNaN(formData.defaultrate) || parseFloat(formData.defaultrate) < 0)) {
    newErrors.defaultrate = true;
    }

    if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    showToast("Validation Error", "Please fill all required fields correctly", "warning");
    return false;
    }
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    floor_name_id: '',
    roomtype: '',
    capacity: '',
    exbedcapability: '1',
    defaultrate: '',
    roomdescription: '',
    status: '1'
    });
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));
    
    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
    formDataToSend.append('branch_id', selectedBranch);
    } else {
    formDataToSend.append('branch_id', getUserDetails("branchId"));
    }
    
    // Append all form data
    Object.keys(formData).forEach(key => {
    formDataToSend.append(key, formData[key]);
    });

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }

    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APIRoomStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APIRoomUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APIRoomDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleFormDataReset();
        handleDialogClose();
        fetchData();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Room Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
    </Typography>
    
    {/* Display selected branch name */}
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        flexWrap: 'wrap' 
        }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Add New Room
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Select Branch
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={() => setFilterOptions(true)}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Export Data
            </Button>
            )}
            
            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search Rooms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {(filters.floor_name_id || filters.roomtype || filters.capacity || filters.dateFrom || filters.dateTo || filters.status) && (
        <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.floor_name_id && (
            <Chip
                label={`Floor: ${getFloorNameById(filters.floor_name_id) || 'N/A'}`}
                onDelete={() => setFilters(prev => ({ ...prev, floor_name_id: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.roomtype && (
            <Chip
                label={`Type: ${filters.roomtype}`}
                onDelete={() => setFilters(prev => ({ ...prev, roomtype: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.capacity && (
            <Chip
                label={`Capacity: ${filters.capacity}`}
                onDelete={() => setFilters(prev => ({ ...prev, capacity: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateFrom && (
            <Chip
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateTo && (
            <Chip
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            <Button
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ minWidth: 'auto' }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Floor</TableCell>
                <TableCell>Room Name</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Default Rate</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((room, index) => (
                <TableRow
                    key={room.id || index}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>{formatCreatedAtDate(room.created_at)}</TableCell>
                    <TableCell>
                    <Chip
                        label={getFloorNameById(room.floor_name_id) || 'N/A'}
                        color="info"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                    {room.roomtype}
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={room.capacity}
                        color="info"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {formatNumberWithComma(room.defaultrate)}
                    </TableCell>
                    <TableCell>
                    {room.status === 1 ? (
                        <Chip
                        label={getStatusById(room.status)}
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                        />
                    ) : (
                        <Chip
                        label={getStatusById(room.status)}
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                        />
                    )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                    <TableCell>
                        <Chip
                        label={getCategoriesNameById(room.branch_id, branchNamesFilterData)}
                        color="info"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    )}
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(room)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(room)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(room)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>
        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Search sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No rooms found</Typography>
            <Typography variant="body2">Try adjusting your search query or select a branch</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {branchNamesFilterData.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter rooms only from that branch. 
            Leave unselected to view all rooms.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                fetchData(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={() => setFilterOptions(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Rooms
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Rooms
            </Button>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Floor
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Floor</InputLabel>
            <Select
                name="floor_name_id"
                value={filters.floor_name_id}
                onChange={handleFilterChange}
                label="Floor"
            >
                <MenuItem value="">All Floors</MenuItem>
                {categoryNamesData.map((floor) => (
                <MenuItem key={floor.id} value={floor.id}>
                    {floor.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Room Name
            </Typography>

            <TextField
            fullWidth
            label="Room Name"
            name="roomtype"
            value={filters.roomtype}
            onChange={handleFilterChange}
            />

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Capacity
            </Typography>

            <TextField
            fullWidth
            label="Capacity"
            name="capacity"
            type="number"
            value={filters.capacity}
            onChange={handleFilterChange}
            />

            <Divider>OR</Divider>
            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Status
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                autoComplete="off"
                onChange={handleFilterChange}
                label="Status"
                required
            >
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setFilterOptions(false)}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={() => setFilterOptions(false)}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        sx={{
        '& .MuiDialog-paper': {
            maxHeight: '90vh',
            height: 'auto',
        }
        }}
    >
        <DialogTitle sx={{
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "Add New Room" : "Edit Room"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            <Paper sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 3,
            p: 2
            }}>
            <Typography variant="subtitle1" sx={{
                fontWeight: 'semibold',
                mb: 2,
                fontSize: '1.1rem'
            }}>
                Room Information
            </Typography>
            <Box>
                {/* Floor Name Selection - First field */}
                <Box>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Floor Category *</InputLabel>
                    <Select
                        name="floor_name_id"
                        value={formData.floor_name_id}
                        autoComplete="off"
                        onChange={handleInputChange}
                        label="Floor Name *"
                        error={errors.floor_name_id}
                        startAdornment={<Layers sx={{ mr: 1 }} />}
                    >
                        <MenuItem value="">
                        <em>Select Floor</em>
                        </MenuItem>
                        {categoryNamesData.map((floor) => (
                        <MenuItem key={floor.id} value={floor.id}>
                            {floor.name}
                        </MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Room Name *"
                    name="roomtype"
                    value={formData.roomtype}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.roomtype}
                    helperText={errors.roomtype ? "Room name is required" : ""}
                    placeholder="Room Name"
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Capacity *"
                    name="capacity"
                    type="number"
                    value={formData.capacity}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.capacity}
                    helperText={errors.capacity ? "Valid capacity is required" : ""}
                    placeholder="Capacity"
                    required
                    inputProps={{ min: 1 }}
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Extra Capability *</InputLabel>
                    <Select
                        name="exbedcapability"
                        value={formData.exbedcapability}
                        autoComplete="off"
                        onChange={handleInputChange}
                        label="Extra Capability *"
                    >
                        {extraCapabilityOptions.map((option, index) => (
                        <MenuItem key={index} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Default Rate *"
                    name="defaultrate"
                    type="number"
                    value={formData.defaultrate}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.defaultrate}
                    helperText={errors.defaultrate ? "Valid rate is required" : ""}
                    placeholder="Rate"
                    required
                    inputProps={{ min: 0, step: "0.01" }}
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Status *</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        autoComplete="off"
                        onChange={handleInputChange}
                        label="Status *"
                    >
                        {statusData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Room Description *"
                    name="roomdescription"
                    multiline
                    rows={3}
                    value={formData.roomdescription}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.roomdescription}
                    helperText={errors.roomdescription ? "Room description is required" : ""}
                    placeholder="Room Description"
                    required
                    />
                </Grid>
                </Box>
            </Box>
            </Paper>
        </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        py: 2,
        justifyContent: 'space-between'
        }}>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
        </Box>
        <Box>
            <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            >
            {postStatus === "ADD" ? "Add Room" : "Update Room"}
            </Button>
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Room Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Floor: </b><br />
                    <Chip
                    label={getFloorNameById(selectedRecord.floor_name_id) || 'N/A'}
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 0.5 }}
                    />
                </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Room Name: </b><br />{selectedRecord.roomtype}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Capacity: </b><br />{selectedRecord.capacity} persons</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Extra Capability: </b><br />
                    {selectedRecord.exbedcapability === '1' ? 'Yes' : 'No'}
                </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Default Rate: </b><br />{formatNumberWithComma(selectedRecord.defaultrate)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Status</Typography>
                {selectedRecord.status === 1 ? (
                    <Chip
                    label={getStatusById(selectedRecord.status)}
                    size="small"
                    color="primary"
                    sx={{ mt: 0.5 }}
                    />
                ) : (
                    <Chip
                    label={getStatusById(selectedRecord.status)}
                    size="small"
                    color="error"
                    sx={{ mt: 0.5 }}
                    />
                )}
                </Grid>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                    <b>Branch: </b><br />
                    <Chip
                        label={getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5 }}
                    />
                    </Typography>
                </Grid>
                )}
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Room Description: </b><br />
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    {selectedRecord.roomdescription}
                    </Paper>
                </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Created Date: </b><br />{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Last Updated: </b><br />{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                </Grid>
            </Grid>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Record</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this room record?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedRecord.roomtype}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Floor: {getFloorNameById(selectedRecord.floor_name_id) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Capacity: {selectedRecord.capacity} persons | Rate: {formatNumberWithComma(selectedRecord.defaultrate)}
            </Typography>
            {hasPermission(PERMISSIONS.BRANCHES) && (
                <Typography variant="body2" color="text.secondary">
                Branch: {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
                </Typography>
            )}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default Room;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});